import * as React from 'react';
import './Login.scss';
import { FC, useEffect } from "react";
import { Input, Spinner, Container, Modal, ModalHeader, ModalBody, ModalFooter, NavLink } from "reactstrap";
import { TK } from '../../store/translations/translationKeys';
import { getLanguage, useTranslations } from '../../store/translations/hooks';
import { ifEnter } from '../../shared/utils';
import AppNameIcon from '../../shared/icons/AppNameIcon';
import AppIcon from '../../shared/icons/AppIcon';
import { useLocation, useHistory } from 'react-router-dom';
import { getCompanyPublicDataAsync } from '../../ajax/requests';
import { BusinessInfo, CompanyPublicData, LoggedUser, MfaRequiredResponse, MfaMethodEnum } from '../../models';
import { Flag } from '../../components/Flag';
import { GoogleSignInButton } from '../../components/OAuthButtons';
import { BusinessSelectionModal } from '../../components/BusinessSelectionModal';
import styled from 'styled-components';
import { paths } from '../../app/constants';
import { clearAuthContext } from '../../shared/authContext';
import { ProfessionalOnboardingWizard, OnboardingData } from '../../components/ProfessionalOnboardingWizard/ProfessionalOnboardingWizard';
import { TextInputField } from '../../components/Forms/fields';
import { MfaVerificationModal } from '../../components/MfaVerificationModal';
import { isMfaRequired } from '../../store/session/actions';
import TurnstileWidget from '../../components/TurnstileWidget';
import AppButton from '../../components/AppButton';


export interface LoginModalStateProps {
    isLoggingIn: boolean,
}

export interface LoginModalDispatchProps {
    onSubmit: (username: string, password: string, isClient: boolean, companyId?: string, impersonateUsername?: string) => Promise<LoggedUser | MfaRequiredResponse | null>,
    onResetPassword: (username: string, isClient: boolean) => void,
    onGoogleSignIn?: (idToken: string, isClient: boolean, companyId?: number) => Promise<LoggedUser | null>,
    onCompleteOnboarding?: (data: OnboardingData, auth: { googleToken?: string; username?: string; password?: string }) => Promise<'loggedIn' | null>,
    onVerifyMfa?: (sessionId: string, otp: string, isClient: boolean, mfaMethod?: MfaMethodEnum) => Promise<LoggedUser | null>,
    googleClientId?: string,
}

export interface LoginModalOwnProps {
    /** The authentication context - determines if logging in as professional or client */
    context: 'professional' | 'client';
}

export type LoginModalProps = LoginModalStateProps & LoginModalDispatchProps & LoginModalOwnProps;

const Divider = styled.div`
    display: flex;
    align-items: center;
    text-align: center;
    margin: var(--spacer-M) 0;
    color: var(--text-muted);
    font-size: 0.9rem;

    &::before,
    &::after {
        content: '';
        flex: 1;
        border-bottom: var(--border-thin) solid var(--appLigthGrey);
    }

    &::before {
        margin-right: var(--spacer-S);
    }

    &::after {
        margin-left: var(--spacer-S);
    }
`;

const RegisterLink = styled.button<{ onClick: () => void }>`
    background: none;
    border: none;
    color: var(--appBlue);
    font-size: 0.85rem;
    cursor: pointer;
    padding: 0;
    margin-left: var(--spacer-XXS);
    text-decoration: none;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.8;
        text-decoration: none;
    }
`;

const Title = styled.h2`
    text-align: center;
    margin-bottom: var(--spacer-2M);
    font-size: 2rem;
    font-weight: 600;
    color: var(--text-body);
`;

const LanguageFlagWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: var(--spacer-XS);
    margin-top: var(--spacer-S);
`;

const BackButton = styled.button<{ 'aria-label': string }>`
    position: absolute;
    top: var(--spacer-XS);
    left: var(--spacer-XS);
    background: none;
    border: none;
    font-size: 1.5rem;
    color: var(--text-muted);
    cursor: pointer;
    padding: 0;
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;
    z-index: 10;

    &:hover {
        color: var(--text-body);
    }
`;

const ForgotPasswordLink = styled.button<{ onClick: () => void }>`
    background: none;
    border: none;
    color: var(--appBlue);
    font-size: 0.85rem;
    cursor: pointer;
    padding: 0;
    text-decoration: none;
    transition: opacity 0.2s;
    margin-bottom: var(--spacer-XXS);
    display: inline-block;

    &:hover {
        opacity: 0.8;
        text-decoration: underline;
    }
`;

const LinksContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacer-XS);
    margin-top: var(--spacer-S);
`;

const GoogleButtonWrapper = styled.div`
    width: 100%;
    height: 45px;
    display: flex;
    justify-content: center;
    margin-bottom: var(--spacer-S);
`;

const CreateAccountHelp = styled.div`
    font-size: 0.85rem;
    color: var(--text-muted);
    text-align: center;
`;

const LoginModal: FC<LoginModalProps> = (props) => {

    const {
        isLoggingIn,
        onSubmit,
        onResetPassword,
        onGoogleSignIn,
        onCompleteOnboarding,
        onVerifyMfa,
        googleClientId,
        context,
    } = props;

    const t = useTranslations();
    const location = useLocation();
    const history = useHistory();
    const [ username, setUsername ] = React.useState('');
    const [ password, setPassword ] = React.useState('');
    const [ resetPasswordModal, setResetPasswordModal ] = React.useState(false);
    const [ companyData, setCompanyData ] = React.useState<CompanyPublicData|undefined>(undefined);
    const [ isOnboardingOpen, setIsOnboardingOpen ] = React.useState(false);
    const [ isOnboardingLoading, setIsOnboardingLoading ] = React.useState(false);
    const [ pendingGoogleToken, setPendingGoogleToken ] = React.useState<string | null>(null);
    const [ pendingCredentials, setPendingCredentials ] = React.useState<{ username: string; password: string } | null>(null);
    const [ pendingBusinesses, setPendingBusinesses ] = React.useState<BusinessInfo[]>([]);
    const [ pendingImpersonateUsernames, setPendingImpersonateUsernames ] = React.useState<string[]>([]);
    const [ pendingCompanyIdForImpersonation, setPendingCompanyIdForImpersonation ] = React.useState<number | undefined>(undefined);
    const [ isGoogleSignInLoading, setIsGoogleSignInLoading ] = React.useState(false);
    const [ googleUserInfo, setGoogleUserInfo ] = React.useState<{ email?: string; name?: string; companyName?: string; photoUrl?: string } | undefined>(undefined);
    const [ mfaSession, setMfaSession ] = React.useState<MfaRequiredResponse | null>(null);
    const [ isMfaVerifying, setIsMfaVerifying ] = React.useState(false);

    const [_, companyId] = location.hash.split('#')
    const queryParams = new URLSearchParams(location.search);
    const urlPartnerCode = queryParams.get('partnerCode') || undefined;
    const urlPartnerId = queryParams.get('partnerId') || undefined;

    const canSubmit = !isLoggingIn && username.length >= 3 && password.length >= 3;
    const isClient = context === 'client';
    const parsedCompanyId = companyId ? parseInt(companyId, 10) : undefined;
    const numericCompanyId = Number.isNaN(parsedCompanyId) ? undefined : parsedCompanyId;
    const supportedLanguages = ['pt', 'en', 'fr'] as const;
    const currentLanguage = getLanguage();

    useEffect(() => {
        if (context !== 'professional') return;
        if (location.pathname.includes('/login/professional/onboarding')) {
            setIsOnboardingOpen(true);
        }
    }, [context, location.pathname]);

    const submitPress = async () => {
        const response = await onSubmit(username, password, isClient, companyId);

        if (!response) {
            // Error already handled by action creator (alert shown)
            return;
        }

        // Check if MFA is required
        if (isMfaRequired(response)) {
            setMfaSession(response);
            return;
        }

        const user = response as LoggedUser;

        // Handle client with no associated clinics
        if (isClient && (!user.businesses || user.businesses.length === 0)) {
            history.push(paths.noClinics());
            return;
        }

        // Handle professional multi-tenant selection (clients can have multiple businesses and see tabs)
        if (!isClient && user.businesses && user.businesses.length > 1) {
            setPendingCredentials({ username, password });
            setPendingBusinesses(user.businesses);
        }
    };

    const handleMfaVerify = async (otp: string) => {
        if (!mfaSession || !onVerifyMfa) return;

        setIsMfaVerifying(true);
        const user = await onVerifyMfa(mfaSession.sessionId, otp, isClient, mfaSession.mfaMethod);
        setIsMfaVerifying(false);

        if (!user) {
            // Error already handled by action creator (alert shown)
            return;
        }

        setMfaSession(null);

        // Handle client with no associated clinics
        if (isClient && (!user.businesses || user.businesses.length === 0)) {
            history.push(paths.noClinics());
            return;
        }

        // Handle professional multi-tenant selection
        if (!isClient && user.businesses && user.businesses.length > 1) {
            setPendingCredentials({ username, password });
            setPendingBusinesses(user.businesses);
        }
    };

    const handleMfaCancel = () => {
        setMfaSession(null);
    };

    const handleGoogleSuccess = async (credential: string) => {
        if (!onGoogleSignIn) return;

        try {
            setIsGoogleSignInLoading(true);
            setPendingBusinesses([]);
            setPendingCredentials(null);
            setPendingGoogleToken(credential);
            const user = await onGoogleSignIn(credential, isClient, numericCompanyId);

            if (!user) {
                // Error already handled by action creator (alert shown)
                return;
            }

            // Handle professional onboarding
            if (user.needsOnboarding && !isClient) {
                setGoogleUserInfo({ email: user.email, name: user.name, photoUrl: user.photoUrl });
                setIsOnboardingOpen(true);
                return;
            }

            // Handle client with no associated clinics
            if (isClient && (!user.businesses || user.businesses.length === 0)) {
                history.push(paths.noClinics());
                return;
            }

            // Handle professional multi-tenant selection (clients can have multiple businesses and see tabs)
            if (!isClient && user.businesses && user.businesses.length > 1) {
                setPendingBusinesses(user.businesses);
            }
        } catch (error) {
            // Errors are already handled by action creators
            console.error('Google Sign-In error:', error);
        } finally {
            setIsGoogleSignInLoading(false);
        }
    };

    const handleBackToChooser = () => {
        clearAuthContext();
        history.push(paths.login() + '/chooser' + location.hash);
    };

    const handleCreateAccount = () => {
        if (context === 'professional') {
            setIsOnboardingOpen(true);
        }
        // Clients cannot create accounts - they must be invited by their clinic
    };

    const handleOnboardingComplete = async (data: OnboardingData) => {
        if (!onCompleteOnboarding) return;
        setIsOnboardingLoading(true);
        const auth = pendingGoogleToken
            ? { googleToken: pendingGoogleToken }
            : {}; // Credentials are now embedded in OnboardingData
        const result = await onCompleteOnboarding(data, auth);
        setIsOnboardingLoading(false);
        if (result) {
            setIsOnboardingOpen(false);
        }
        if (result === 'loggedIn') {
            setPendingGoogleToken(null);
        }
    };

    const handleOnboardingCancel = () => {
        setIsOnboardingOpen(false);
        setPendingGoogleToken(null);
        setGoogleUserInfo(undefined);
    };

    const handleOnboardingGoogleCredentialSelect = (credential: string, userInfo?: { email?: string; name?: string; photoUrl?: string }) => {
        setPendingGoogleToken(credential);
        if (userInfo) {
            setGoogleUserInfo(prev => ({ ...(prev || {}), ...userInfo }));
        }
    };

    const handleOnboardingGoogleCredentialClear = () => {
        setPendingGoogleToken(null);
        setGoogleUserInfo(undefined);
    };

    const handleSelectBusiness = async (businessId: string) => {
        const selectedCompanyId = parseInt(businessId, 10);
        const companyIdArg = Number.isNaN(selectedCompanyId) ? undefined : selectedCompanyId;

        // Check if the selected business requires user impersonation
        const selectedBusiness = pendingBusinesses.find(b => b.id === businessId);
        const usernames = selectedBusiness?.usernames ?? [];
        setPendingBusinesses([]);

        if (usernames.length > 1) {
            setPendingCompanyIdForImpersonation(companyIdArg);
            setPendingImpersonateUsernames(usernames);
            return;
        }

        const impersonateUsername = usernames.length === 1 ? usernames[0] : undefined;
        await authenticateWithCompany(companyIdArg, impersonateUsername);
    };

    const handleSelectUser = async (selectedUsername: string) => {
        const companyIdArg = pendingCompanyIdForImpersonation;
        setPendingImpersonateUsernames([]);
        setPendingCompanyIdForImpersonation(undefined);
        await authenticateWithCompany(companyIdArg, selectedUsername);
    };

    const authenticateWithCompany = async (companyIdArg: number | undefined, impersonateUsername?: string) => {
        let user: LoggedUser | null = null;

        if (pendingGoogleToken && onGoogleSignIn) {
            user = await onGoogleSignIn(pendingGoogleToken, isClient, companyIdArg);
        } else if (pendingCredentials) {
            const response = await onSubmit(pendingCredentials.username, pendingCredentials.password, isClient, String(companyIdArg), impersonateUsername);
            if (response && !isMfaRequired(response)) {
                user = response as LoggedUser;
            }
        }

        if (!user) return;

        setPendingCredentials(null);

        // Handle professional onboarding
        if (user.needsOnboarding && !isClient) {
            setGoogleUserInfo({ email: user.email, name: user.name, photoUrl: user.photoUrl });
            setIsOnboardingOpen(true);
            return;
        }

        // Handle client with no associated clinics
        if (isClient && (!user.businesses || user.businesses.length === 0)) {
            history.push(paths.noClinics());
            return;
        }

        // Handle professional multi-tenant selection (clients can have multiple businesses and see tabs)
        if (!isClient && user.businesses && user.businesses.length > 1) {
            setPendingBusinesses(user.businesses);
        }
    };

    useEffect(() => {
        if (!companyId) return;
        getCompanyPublicDataAsync(companyId).then(setCompanyData)
    }, [companyId])    

    return (
        <div className="loginPage">
            <div className="centerContainer">
                <a href="https://appointmint.pt/"><AppNameIcon className="appNameIcon"/></a>
                <AppIcon className="appIcon appIcon1" />
                <AppIcon className="appIcon appIcon2" />

                <div className="loginForm" style={{position: 'relative'}}>

                    <BackButton aria-label={t(TK.back)} onClick={handleBackToChooser}>
                        ←
                    </BackButton>

                    {!!companyId && !!companyData?.logo && (
                        <div className="loginCompanyLogo">
                            <img src={companyData.logo} alt={companyData.name || t(TK.appointmint)} />
                        </div>
                    )}

                    <Title>
                        {context === 'professional' ? t(TK.professional) : t(TK.client)}
                    </Title>

                    {googleClientId && onGoogleSignIn && (
                        <>
                            <GoogleButtonWrapper>
                                <GoogleSignInButton
                                    clientId={googleClientId}
                                    onSuccess={handleGoogleSuccess}
                                    onError={(error) => console.error('Google Sign-In error:', error)}
                                    disabled={isLoggingIn || isGoogleSignInLoading}
                                />
                            </GoogleButtonWrapper>
                            <Divider>{t(TK.or)}</Divider>
                        </>
                    )}

                    <TextInputField
                        id="username"
                        type={isClient ? 'email' : 'text'}
                        placeholder={t(TK.usernameOrEmail)}
                        value={username}
                        onChange={e => setUsername(e)}
                        onKeyUp={e => ifEnter(e, submitPress)}
                        style={{ marginBottom: '0.7rem' }}
                        aria-label={t(TK.usernameOrEmail)}
                    />
                    <TextInputField
                        id="password"
                        type="password"
                        placeholder={t(TK.yourPassword)}
                        value={password}
                        onChange={e => setPassword(e)}
                        onKeyUp={e => ifEnter(e, submitPress)}
                        style={{ marginBottom: '1rem' }}
                        aria-label={t(TK.yourPassword)}
                    />

                    <TurnstileWidget />
                    
                    <AppButton id="enter-button" className="buttonSubmit" onClick={submitPress} disabled={!canSubmit}>
                        <Spinner size="sm" className="spinner" style={{ display: isLoggingIn ? 'inline-block' : 'none' }} />
                        {t(TK.login)}
                    </AppButton>

                    <LinksContainer>
                        <ForgotPasswordLink onClick={() => setResetPasswordModal(true)}>
                            {t(TK.forgotPassword)}
                        </ForgotPasswordLink>
                        {context === 'professional' && (
                            <RegisterLink id="create-account-button" onClick={handleCreateAccount}>
                                {t(TK.createAccount)}
                            </RegisterLink>
                        )}
                        {context === 'client' && (
                            <CreateAccountHelp>{t(TK.createAccountWithGoogle)}</CreateAccountHelp>
                        )}
                    </LinksContainer>

                    <LanguageFlagWrapper>
                        {supportedLanguages
                                .filter(lang => lang !== currentLanguage)
                                .map(lang => (
                                    <NavLink key={lang} href={`/${lang}`}>
                                    <Flag flag={lang} />
                                </NavLink>
                            ))}
                    </LanguageFlagWrapper>
                </div>
            </div>
            
            <Modal isOpen={resetPasswordModal} toggle={() => setResetPasswordModal(!resetPasswordModal)}>
                <ModalHeader toggle={() => setResetPasswordModal(!resetPasswordModal)}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.newPasswordWillBeSent)}</ModalBody>
                <ModalFooter>
                    <AppButton id="reset-password-button" color="danger" onClick={() => { onResetPassword(username, isClient); setResetPasswordModal(false); }}>{t(TK.resetPassword)}</AppButton>{' '}
                    <AppButton id="reset-password-cancel-button" color="secondary" onClick={() => setResetPasswordModal(false)}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>
            <BusinessSelectionModal
                isOpen={pendingBusinesses.length > 0}
                businesses={pendingBusinesses.map(business => ({
                    id: business.id,
                    name: business.name,
                    logo: business.logo
                }))}
                onSelectBusiness={handleSelectBusiness}
            />
            <BusinessSelectionModal
                isOpen={pendingImpersonateUsernames.length > 0}
                businesses={pendingImpersonateUsernames.map(u => ({ id: u, name: u }))}
                onSelectBusiness={handleSelectUser}
                title={t(TK.selectYourUser)}
            />
            <ProfessionalOnboardingWizard
                isOpen={isOnboardingOpen}
                isLoading={isOnboardingLoading}
                onComplete={handleOnboardingComplete}
                onCancel={handleOnboardingCancel}
                onGoogleCredentialSelect={handleOnboardingGoogleCredentialSelect}
                onGoogleCredentialClear={handleOnboardingGoogleCredentialClear}
                googleClientId={googleClientId}
                initialData={{ ...googleUserInfo, partnerCode: urlPartnerCode, partnerId: urlPartnerId }}
            />
            <MfaVerificationModal
                isOpen={mfaSession !== null}
                isLoading={isMfaVerifying}
                mfaMethod={mfaSession?.mfaMethod ?? MfaMethodEnum.None}
                maskedPhoneNumber={mfaSession?.maskedPhoneNumber}
                onVerify={handleMfaVerify}
                onCancel={handleMfaCancel}
            />
        </div>
  );
}

export default LoginModal;
